import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import './Signup.css';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  // Group roles by categories
  const roleCategories = {
    'Production': ['designer', 'machine-operator', 'carpenter','wood-polisher', 'assistant-carpenter', 'electrician', 'quality-control'],
    'Management': ['workshop-manager', 'store-manager','managing-director', 'manager', 'ceo'],
    'Administration': ['accountant', 'hr', 'hr-admin', 'marketing-sales'],
    'Distribution': ['distributor']
  };

  const allRoles = [
    { id: 'designer', label: 'Designer', icon: '🎨', category: 'Production' },
    { id: 'machine-operator', label: 'Machine Technician', icon: '⚙️', category: 'Production' },
    { id: 'carpenter', label: 'Carpenter', icon: '🔨', category: 'Production' },
    { id: 'assistant-carpenter', label: 'Assistant Carpenter', icon: '🔨', category: 'Production' },
    { id: 'electrician', label: 'Electrician', icon: '⚡', category: 'Production' },
  
    { id: 'workshop-manager', label: 'Workshop Manager', icon: '🏭', category: 'Management' },
    { id: 'store-manager', label: 'Store Manager', icon: '📦', category: 'Management' },
    { id: 'hr', label: 'HR Manager', icon: '👥', category: 'Administration' },
    { id: 'hr-admin', label: 'HR Admin', icon: '📋', category: 'Administration' },
    { id: 'wood-polisher', label: 'Wood Polisher', icon: '🧹', category: 'Production' },
    { id: 'distributor', label: 'Distributor', icon: '🚚', category: 'Distribution' },
    { id: 'marketing-sales', label: 'Marketing & Sales', icon: '📈', category: 'Administration' }
  ];

     
    // { id: 'manager', label: 'Manager', icon: '👨‍💼', category: 'Management' },
    // { id: 'ceo', label: 'CEO', icon: '👔', category: 'Management' },
    // { id: 'managing-director', label: 'Managing Director', icon: '👔', category: 'Management' },
    // { id: 'accountant', label: 'Accountant', icon: '📊', category: 'Administration' },
    // { id: 'quality-control', label: 'Quality Control', icon: '🎛️', category: 'Production' },

  // Filter roles based on selected category
  const filteredRoles = selectedCategory === 'all' 
    ? allRoles 
    : allRoles.filter(role => role.category === selectedCategory);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        navigate('/dashboard');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.role) {
        alert('Please select your role');
        setLoading(false);
        return;
      }

      if (!formData.name.trim()) {
        alert('Please enter your name');
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: formData.role,
      });

      await setDoc(doc(db, 'users', user.uid), {
        name: formData.name.trim(),
        email: formData.email,
        role: formData.role,
        createdAt: new Date(),
        status: 'active',
        lastLogin: new Date()
      });

      navigate('/login');

    } catch (error) {
      console.error('Signup error:', error);
      alert(error.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({ ...prev, role }));
  };

  // Get count of roles in each category
  const getCategoryCount = (category) => {
    return allRoles.filter(role => role.category === category).length;
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        {/* Header */}
        <div className="signup-header">
          <div className="signup-logo-circle">
            <span className="signup-logo-text">KG</span>
          </div>
          <h1 className="signup-company-name">Kajja Galleries</h1>
          <p className="signup-subtitle">Create Your Account</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="signup-form-group">
            <label className="signup-form-label">Full Name</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="signup-form-input"
              placeholder="Enter your full name"
            />
          </div>

          <div className="signup-form-group">
            <label className="signup-form-label">Email Address</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="signup-form-input"
              placeholder="Enter your email"
            />
          </div>

          <div className="signup-form-group">
            <label className="signup-form-label">Password</label>
            <input
              type="password"
              name="password"
              required
              minLength="6"
              value={formData.password}
              onChange={handleInputChange}
              className="signup-form-input"
              placeholder="Create a password (minimum 6 characters)"
            />
          </div>

          {/* Role Selection - Improved Compact Design */}
          <div className="signup-role-section">
            <div className="signup-role-label">Select Your Role</div>
            
            {/* Category Tabs */}
            <div className="signup-role-categories">
              <button
                type="button"
                className={`signup-category-tab ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All Roles ({allRoles.length})
              </button>
              {Object.keys(roleCategories).map(category => (
                <button
                  key={category}
                  type="button"
                  className={`signup-category-tab ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category} ({getCategoryCount(category)})
                </button>
              ))}
            </div>

            {/* Roles Grid */}
            <div className="signup-role-grid">
              {filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className={`signup-role-card ${formData.role === role.id ? 'selected' : ''}`}
                  onClick={() => handleRoleSelect(role.id)}
                >
                  <div className="signup-role-icon">{role.icon}</div>
                  <div className="signup-role-name">{role.label}</div>
                  {formData.role === role.id && (
                    <div className="signup-role-check">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !formData.name || !formData.email || !formData.password || !formData.role}
            className="signup-submit-btn"
          >
            {loading ? (
              <>
                <span className="signup-loading"></span>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Links */}
        <div className="signup-links">
          <button 
            onClick={() => navigate('/login')} 
            className="signup-link"
            type="button"
          >
            Already have an account? Login
          </button>
        </div>

        {/* Footer */}
        <div className="signup-footer">
          © 2024 Kajja Galleries. Secure access only.
        </div>
      </div>
    </div>
  );
};

export default Signup;